import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, ArrowRight } from 'lucide-react';
import { blogArticles } from '../data/blogContent';
import SEO, { buildArticleJsonLd, buildBreadcrumbJsonLd } from '../components/SEO';
import NewsletterSignup from '../components/NewsletterSignup';

// Render inline markdown: **bold**, *italic*
const renderInline = (text) => {
  // Split on bold/italic tokens while preserving them
  const tokens = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return tokens.map((token, i) => {
    if (/^\*\*[^*]+\*\*$/.test(token)) {
      return <strong key={i}>{token.slice(2, -2)}</strong>;
    }
    if (/^\*[^*\n]+\*$/.test(token)) {
      return <em key={i}>{token.slice(1, -1)}</em>;
    }
    return token;
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const articleUrl = `https://nutricentreriviera.ch/blog/${article.slug}`;
  const articleJsonLd = buildArticleJsonLd({
    title: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    url: articleUrl,
    author: article.author
  });
  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: "Accueil", url: "https://nutricentreriviera.ch/" },
    { name: "Blog", url: "https://nutricentreriviera.ch/blog" },
    { name: article.title, url: articleUrl }
  ]);

  return (
    <div style={{ marginTop: '100px' }}>
      <SEO
        title={article.metaTitle || `${article.title} | Harmonie Féminine`}
        description={article.metaDescription || article.excerpt}
        url={articleUrl}
        type="article"
        image={article.image}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <section style={{ background: 'var(--gradient-hero)', padding: '4rem 0' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <Link to="/blog" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--accent-primary)',
            marginBottom: '2rem',
            textDecoration: 'none',
            fontSize: '0.95rem',
            fontWeight: 500
          }}>
            <ArrowLeft size={18} />
            Retour au blog
          </Link>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <span className="caption" style={{
              background: 'var(--accent-primary-light)',
              color: 'var(--accent-primary)',
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem'
            }}>
              {article.category}
            </span>
            <span className="body-small" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--text-muted)'
            }}>
              <Calendar size={14} />
              {new Date(article.date).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
          <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
            {article.title}
          </h1>
          <p className="heading-subtitle">
            {article.excerpt}
          </p>
        </div>
      </section>

      <section style={{ background: 'white', padding: '3rem 0' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{
            height: '400px',
            borderRadius: '1rem',
            overflow: 'hidden',
            marginBottom: '3rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
          }}>
            <img
              src={article.image}
              alt={article.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
          loading="lazy"
          decoding="async"/>
          </div>

          <article style={{
            fontSize: '1.05rem',
            lineHeight: '1.8',
            color: 'var(--text-secondary)'
          }}>
            {article.content.split('\n\n').map((paragraph, index) => {
              const trimmed = paragraph.trim();
              if (trimmed.startsWith('# ')) {
                return <h1 key={index} className="heading-1" style={{ marginTop: '3rem', marginBottom: '1.5rem' }}>{renderInline(trimmed.substring(2))}</h1>;
              } else if (trimmed.startsWith('## ')) {
                return <h2 key={index} className="heading-2" style={{ marginTop: '2.5rem', marginBottom: '1rem' }}>{renderInline(trimmed.substring(3))}</h2>;
              } else if (trimmed.startsWith('### ')) {
                return <h3 key={index} className="heading-3" style={{ marginTop: '2rem', marginBottom: '0.75rem' }}>{renderInline(trimmed.substring(4))}</h3>;
              } else if (trimmed.startsWith('> ')) {
                const quote = trimmed.replace(/^>\s?/gm, '');
                return (
                  <blockquote key={index} style={{
                    borderLeft: '3px solid var(--accent-primary)',
                    paddingLeft: '1.25rem',
                    margin: '2rem 0',
                    fontStyle: 'italic',
                    color: 'var(--accent-primary)',
                    fontSize: '1.15rem',
                    lineHeight: 1.6
                  }}>
                    {renderInline(quote)}
                  </blockquote>
                );
              } else if (/^(- |\* )/m.test(trimmed) && trimmed.split('\n').every(l => /^(- |\* )/.test(l.trim()) || l.trim() === '')) {
                const items = trimmed.split('\n').filter(l => l.trim()).map(l => l.trim().replace(/^(- |\* )/, ''));
                return (
                  <ul key={index} style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {items.map((item, i) => (
                      <li key={i} style={{ lineHeight: 1.8 }}>{renderInline(item)}</li>
                    ))}
                  </ul>
                );
              } else {
                // Render paragraph with line breaks preserved and inline markdown
                const lines = trimmed.split('\n');
                return (
                  <p key={index} style={{ marginBottom: '1.5rem' }}>
                    {lines.map((line, i) => (
                      <React.Fragment key={i}>
                        {renderInline(line)}
                        {i < lines.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>
                );
              }
            })}
          </article>
        </div>
      </section>

      <NewsletterSignup />

      <section style={{ background: 'var(--accent-primary)', color: 'white', padding: '4rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-2" style={{ color: 'white', marginBottom: '1rem' }}>
            Prête à passer à l'action ?
          </h2>
          <p className="body-large" style={{ color: 'white', opacity: 0.95, maxWidth: '600px', margin: '0 auto 2.5rem' }}>
            Prenez rendez-vous pour un accompagnement personnalisé sur la Riviera vaudoise
          </p>
          <a
            href="https://www.onedoc.ch/fr/cabinet-de-groupe/vevey/ebdj4/nutri-centre-riviera"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ background: 'white', color: 'var(--accent-primary)', textDecoration: 'none' }}
          >
            Prendre rendez-vous
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
